// Modal for editing an existing draft class session
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Pencil, XCircle } from 'lucide-react'

import { CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { Button } from '@/ui/button'
import { Modal } from '@/ui/modal'
import { useClassSessionStore } from '@/features/classes/stores'
import type { ClassSession } from '@/features/classes/stores'
import { getAllLocations, type Location } from '@/features/classes/api/locationsService'
import { fetchUsers, type PBUser } from '@/core/api/usersService'

import { inputClass } from '../utils/formatters'

interface EditSessionModalProps {
  session: ClassSession
  onClose: () => void
}

export function EditSessionModal({ session, onClose }: EditSessionModalProps) {
  const courses = useClassSessionStore((s) => s.courses)
  const updateSession = useClassSessionStore((s) => s.updateSession)
  const [locations, setLocations] = useState<Location[]>([])
  const [instructors, setInstructors] = useState<PBUser[]>([])
  const [resourcesLoading, setResourcesLoading] = useState(true)
  const [date, setDate] = useState(session.date)
  const [startTime, setStartTime] = useState(session.startTime ?? '')
  const [endTime, setEndTime] = useState(session.endTime ?? '')
  const [maxAttendees, setMaxAttendees] = useState(String(session.maxAttendees ?? 20))
  const [locationId, setLocationId] = useState(session.locationId ?? '')
  const [instructorId, setInstructorId] = useState(session.instructorId ?? '')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getAllLocations().catch(() => []),
      fetchUsers().catch(() => []),
    ]).then(([fetchedLocations, fetchedUsers]) => {
      if (cancelled) {
        return
      }
      setLocations(fetchedLocations)
      setInstructors(fetchedUsers.filter((u) => u.role === 'admin' || u.role === 'instructor'))
      setResourcesLoading(false)
    })
    return () => { cancelled = true }
  }, [session])

  const selectedLocationName = useMemo(() => {
    return locations.find((loc) => loc.id === locationId)?.name ?? session.locationName ?? session.location ?? ''
  }, [locationId, locations, session])

  const selectedInstructorName = useMemo(() => {
    return instructors.find((user) => user.id === instructorId)?.name ?? session.instructorName ?? ''
  }, [instructorId, instructors, session])

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!locationId || !date.trim() || !instructorId) {
      setError('Please complete all required fields.')
      return
    }
    const maxAttendeesNum = Number(maxAttendees)
    if (!Number.isFinite(maxAttendeesNum) || maxAttendeesNum < 1 || maxAttendeesNum > 500) {
      setError('Max attendees must be between 1 and 500.')
      return
    }
    if (endTime && startTime && endTime <= startTime) {
      setError('End time must be after start time.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload: Parameters<typeof updateSession>[1] = {
        date,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        maxAttendees: maxAttendeesNum,
        locationId,
        instructorId,
      }
      await updateSession(session.id, payload)
      onClose()
    } catch {
      setError('Could not update the class. Try again.')
      setSaving(false)
    }
  }

  if (resourcesLoading) {
    return (
      <Modal open onClose={onClose} className="max-w-md p-0">
        <CardContent className="py-8 text-center text-sm text-slate-500">Loading…</CardContent>
      </Modal>
    )
  }

  return (
    <Modal open onClose={onClose} className="max-w-md p-0">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-navy flex items-center gap-2">
          <Pencil className="w-4 h-4 text-primary" />
          Edit Class
        </CardTitle>
        <CardDescription>Update the details for this scheduled class.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <XCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label htmlFor="edit-course" className="block text-sm font-medium text-navy mb-1">Course</label>
            <select id="edit-course" disabled value={session.courseId} className={inputClass}>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">Course type cannot be changed.</p>
          </div>

          <div>
            <label htmlFor="edit-location" className="block text-sm font-medium text-navy mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <select
              id="edit-location"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className={inputClass}
            >
              <option value="">Select a location</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            {selectedLocationName && !locationId && (
              <p className="text-xs text-amber-600 mt-1">Previous location: {selectedLocationName}</p>
            )}
          </div>

            <div>
              <label htmlFor="edit-date" className="block text-sm font-medium text-navy mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                id="edit-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={inputClass}
              />
            </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-start-time" className="block text-sm font-medium text-navy mb-1">Start Time</label>
              <input
                id="edit-start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="edit-end-time" className="block text-sm font-medium text-navy mb-1">End Time</label>
              <input
                id="edit-end-time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="edit-instructor" className="block text-sm font-medium text-navy mb-1">
              Instructor <span className="text-red-500">*</span>
            </label>
            <select
              id="edit-instructor"
              value={instructorId}
              onChange={(e) => setInstructorId(e.target.value)}
              className={inputClass}
            >
              <option value="">Select an instructor</option>
              {instructors.map((user) => (
                <option key={user.id} value={user.id}>{user.name ?? user.email}</option>
              ))}
            </select>
            {selectedInstructorName && !instructorId && (
              <p className="text-xs text-amber-600 mt-1">Previous instructor: {selectedInstructorName}</p>
            )}
          </div>

          <div>
            <label htmlFor="edit-max-attendees" className="block text-sm font-medium text-navy mb-1">
              Max Attendees <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-max-attendees"
              type="number"
              min={1}
              max={500}
              value={maxAttendees}
              onChange={(e) => setMaxAttendees(e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              <Pencil className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Modal>
  )
}
